import { useEffect, useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { Activity, AlertTriangle, LayoutDashboard, LogOut, Menu, Settings, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18nContext';
import LanguageSwitcher from './LanguageSwitcher';

export default function Layout() {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const { t } = useI18n();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const navLinks = [
        { path: '/', label: t('layout.nav.dashboard'), icon: LayoutDashboard },
        { path: '/incidents', label: t('layout.nav.incidents'), icon: AlertTriangle },
        { path: '/status-pages', label: t('layout.nav.statusPages'), icon: Settings },
    ];

    function isActivePath(path) {
        return path === '/'
            ? location.pathname === '/'
            : location.pathname === path || location.pathname.startsWith(`${path}/`);
    }

    function handleLogout() {
        logout();
        navigate('/login', { replace: true });
    }

    useEffect(() => {
        setMobileMenuOpen(false);
    }, [location.pathname]);

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Navegacion superior */}
            <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex min-h-16 items-center justify-between gap-3 py-3 md:h-16 md:py-0">
                        <div className="flex min-w-0 items-center gap-2">
                            <Activity className="h-6 w-6 text-blue-600" />
                            <span className="truncate text-lg font-bold text-gray-900 sm:text-xl">{t('common.appName')}</span>
                        </div>

                        <div className="hidden md:flex items-center gap-3">
                            {navLinks.map((link) => (
                                <Link
                                    key={link.path}
                                    to={link.path}
                                    className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                        isActivePath(link.path)
                                            ? 'bg-blue-50 text-blue-700'
                                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                    }`}
                                >
                                    <link.icon className="h-4 w-4" />
                                    {link.label}
                                </Link>
                            ))}
                            <div className="hidden md:block h-6 w-px bg-gray-200" />
                            <LanguageSwitcher compact className="hidden sm:inline-flex" selectClassName="w-56 max-w-full pr-10" />
                            <div className="hidden sm:block text-right">
                                <div className="text-sm font-medium text-gray-900">{user?.full_name || t('common.userFallback')}</div>
                                <div className="text-xs text-gray-500">{user?.email}</div>
                            </div>
                            <button
                                onClick={handleLogout}
                                className="flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                            >
                                <LogOut className="h-4 w-4" />
                                {t('common.logout')}
                            </button>
                        </div>

                        <button
                            type="button"
                            onClick={() => setMobileMenuOpen((current) => !current)}
                            className="inline-flex items-center gap-2 rounded-md border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 md:hidden"
                            aria-expanded={mobileMenuOpen}
                            aria-label={mobileMenuOpen ? t('common.close') : t('common.menu')}
                        >
                            {mobileMenuOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
                            <span>{mobileMenuOpen ? t('common.close') : t('common.menu')}</span>
                        </button>
                    </div>

                    {mobileMenuOpen && (
                        <div className="border-t border-gray-200 py-4 md:hidden">
                            <div className="flex flex-col gap-2">
                                {navLinks.map((link) => (
                                    <Link
                                        key={link.path}
                                        to={link.path}
                                        className={`flex items-center gap-3 rounded-md px-3 py-3 text-sm font-medium transition-colors ${
                                            isActivePath(link.path)
                                                ? 'bg-blue-50 text-blue-700'
                                                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                        }`}
                                    >
                                        <link.icon className="h-4 w-4" />
                                        {link.label}
                                    </Link>
                                ))}
                            </div>

                            <div className="mt-4 space-y-4 border-t border-gray-200 pt-4">
                                <LanguageSwitcher
                                    className="w-full justify-between"
                                    labelClassName="text-sm font-medium text-gray-700"
                                    selectClassName="w-56 max-w-full pr-10"
                                />

                                <div className="rounded-lg bg-gray-50 px-3 py-3">
                                    <div className="text-sm font-medium text-gray-900">{user?.full_name || t('common.userFallback')}</div>
                                    <div className="mt-1 break-all text-xs text-gray-500">{user?.email}</div>
                                </div>

                                <button
                                    onClick={handleLogout}
                                    className="flex w-full items-center justify-center gap-2 rounded-md bg-gray-100 px-3 py-3 text-sm font-medium text-gray-700 hover:bg-gray-200 transition-colors"
                                >
                                    <LogOut className="h-4 w-4" />
                                    {t('common.logout')}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </nav>

            {/* Contenido principal */}
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <Outlet />
            </main>
        </div>
    );
}
